import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface AppNotification {
  id: string
  type: 'booking' | 'journey' | 'chat' | 'payment' | 'system'
  title: string
  body: string
  data?: Record<string, unknown>
  isRead: boolean
  createdAt: string
}

interface NotificationState {
  notifications: AppNotification[]
  unreadCount: number
  lastFetchedAt: string | null
}

interface NotificationActions {
  addNotification: (notification: Omit<AppNotification, 'id' | 'isRead' | 'createdAt'>) => void
  setNotifications: (notifications: AppNotification[]) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  removeNotification: (id: string) => void
  clearAll: () => void
  setLastFetchedAt: (date: string) => void
}

export const useNotificationStore = create<NotificationState & NotificationActions>()(
  persist(
    (set) => ({
      // Initial state
      notifications: [],
      unreadCount: 0,
      lastFetchedAt: null,

      // Actions
      addNotification: (notification) =>
        set((state) => {
          const newNotification: AppNotification = {
            ...notification,
            id: `notif-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            isRead: false,
            createdAt: new Date().toISOString(),
          }
          return {
            notifications: [newNotification, ...state.notifications],
            unreadCount: state.unreadCount + 1,
          }
        }),

      setNotifications: (notifications) =>
        set({
          notifications,
          unreadCount: notifications.filter((n) => !n.isRead).length,
        }),

      markAsRead: (id) =>
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id)
          if (!notification || notification.isRead) {
            return state
          }
          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, isRead: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          }
        }),

      markAllAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
          unreadCount: 0,
        })),

      removeNotification: (id) =>
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id)
          const wasUnread = notification && !notification.isRead
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          }
        }),

      clearAll: () =>
        set({
          notifications: [],
          unreadCount: 0,
        }),

      setLastFetchedAt: (date) =>
        set({
          lastFetchedAt: date,
        }),
    }),
    {
      name: 'notification-storage',
      partialize: (state) => ({
        notifications: state.notifications.slice(0, 50), // Keep only latest 50
        unreadCount: state.unreadCount,
        lastFetchedAt: state.lastFetchedAt,
      }),
      skipHydration: true,
    }
  )
)
